import React from "react";
import { ArrowRight, CheckCircle2, ChevronUp, LockKeyhole, Route, X } from "lucide-react";
import "./DriverPortalNextStop.css";

function NextStopDialog({ id, label, children, onClose }) {
  const dialogRef = React.useRef(null);
  const closeRef = React.useRef(null);

  React.useEffect(() => {
    const dialog = dialogRef.current;
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";
    closeRef.current?.focus({ preventScroll: true });
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  return (
    <dialog ref={dialogRef} id={id} className="driver-next-dialog" aria-labelledby={`${id}-title`}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose();
      }}>
      <header className="driver-next-dialog__header">
        <h2 id={`${id}-title`}>{label}</h2>
        <button ref={closeRef} type="button" className="driver-next-dialog__close" aria-label="Fermer les détails de la destination" onClick={onClose}>
          <X size={20} aria-hidden="true" />
        </button>
      </header>
      <div className="driver-next-stop driver-next-dialog__content">{children}</div>
    </dialog>
  );
}

export default function DriverPortalNextStop({ label = "Destination suivante", title, kind = "", locked = false, ready = false, readyLabel = "Appuyez pour continuer", onContinue, disabled = false, children }) {
  const [open, setOpen] = React.useState(false);
  const dockRef = React.useRef(null);
  const spaceRef = React.useRef(null);
  const dialogId = React.useId();
  const isReady = ready && !locked;
  const Icon = isReady ? CheckCircle2 : Route;
  const canContinue = isReady && typeof onContinue === "function";
  const [attention, setAttention] = React.useState(false);
  const previousCanContinue = React.useRef(canContinue);
  const pendingAttention = React.useRef(false);

  React.useEffect(() => {
    if (!canContinue) {
      pendingAttention.current = false;
      setAttention(false);
    } else if (!previousCanContinue.current) {
      pendingAttention.current = true;
    }
    previousCanContinue.current = canContinue;
    // Wait until the final confirmation releases the controls.
    if (disabled) {
      setAttention(false);
      return;
    }
    if (pendingAttention.current) {
      pendingAttention.current = false;
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) setAttention(true);
    }
  }, [canContinue, disabled]);

  const ActionIcon = canContinue ? ArrowRight : ChevronUp;
  const destinationPrefix = isReady && kind ? `${label} : ` : "";

  // Reserve the actual height, including wrapped names and the phone's safe area.
  React.useLayoutEffect(() => {
    const dock = dockRef.current;
    const updateHeight = () => {
      spaceRef.current?.style.setProperty("height", `${dock.getBoundingClientRect().height}px`);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(dock);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={spaceRef} className="driver-next-dock-space">
      <div ref={dockRef} className="driver-next-dock">
        <button type="button" disabled={disabled} className={`driver-next-dock__toggle${isReady ? " driver-next-dock__toggle--ready" : ""}${attention && canContinue && !disabled ? " driver-next-dock__toggle--attention" : ""}`} aria-haspopup={canContinue ? undefined : "dialog"} aria-expanded={canContinue ? undefined : open}
          onAnimationEnd={() => setAttention(false)}
          aria-label={`${isReady ? readyLabel : label} — ${destinationPrefix}${kind ? `${kind} · ` : ""}${title}`}
          aria-controls={open ? dialogId : undefined} onClick={canContinue ? onContinue : () => setOpen(true)}>
          <Icon className="driver-next-dock__icon" size={21} aria-hidden="true" />
          <span className="driver-next-dock__copy">
            <span className="driver-next-dock__label" role="status" aria-live="polite" aria-atomic="true">{isReady ? readyLabel : label}</span>
            <span className="driver-next-dock__destination">{destinationPrefix}{kind ? <span>{kind} · </span> : null}<strong>{title}</strong></span>
          </span>
          {locked ? <LockKeyhole className="driver-next-dock__lock" size={15} aria-label="Passage à la destination verrouillé" /> : null}
          <ActionIcon className="driver-next-dock__chevron" size={20} aria-hidden="true" />
        </button>
      </div>
      {open ? <NextStopDialog id={dialogId} label={label} onClose={() => setOpen(false)}>{children(() => setOpen(false))}</NextStopDialog> : null}
    </div>
  );
}
